import { PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef, useState } from 'react'

type Size = { width: number; height: number }
type Position = { x: number; y: number }
type Edge = { top: boolean; right: boolean; bottom: boolean; left: boolean }

interface UseResizeOptions {
  size: Size
  position: Position
  minSize?: Size
  elementRef: React.RefObject<HTMLElement | null>
  onSizeChange?: (size: Size) => void
  onPositionChange?: (position: Position) => void
  enabled?: boolean
  edgeThreshold?: number
}

const DEFAULT_MIN_SIZE: Size = { width: 480, height: 320 }
const EDGE_THRESHOLD = 6
const CORNER_THRESHOLD = 16
const EDGE_MARGIN = 16

function detectEdge(el: HTMLElement, clientX: number, clientY: number, threshold: number): Edge | null {
  const rect = el.getBoundingClientRect()
  const nearTop = clientY - rect.top
  const nearBottom = rect.bottom - clientY
  const nearLeft = clientX - rect.left
  const nearRight = rect.right - clientX

  // Corners use a wider hit zone so they're easier to grab
  const isCorner =
    (nearTop < CORNER_THRESHOLD && nearLeft < CORNER_THRESHOLD) ||
    (nearTop < CORNER_THRESHOLD && nearRight < CORNER_THRESHOLD) ||
    (nearBottom < CORNER_THRESHOLD && nearLeft < CORNER_THRESHOLD) ||
    (nearBottom < CORNER_THRESHOLD && nearRight < CORNER_THRESHOLD)

  const t = isCorner ? CORNER_THRESHOLD : threshold
  const top = nearTop < t
  const bottom = nearBottom < t
  const left = nearLeft < t
  const right = nearRight < t
  if (!top && !bottom && !left && !right) return null
  return { top, right, bottom, left }
}

function cursorForEdge(edge: Edge): string {
  const { top, right, bottom, left } = edge
  if ((top && left) || (bottom && right)) return 'nwse-resize'
  if ((top && right) || (bottom && left)) return 'nesw-resize'
  if (top || bottom) return 'ns-resize'
  return 'ew-resize'
}

export default function useResize({
  size,
  position,
  minSize = DEFAULT_MIN_SIZE,
  elementRef,
  onSizeChange,
  onPositionChange,
  enabled = true,
  edgeThreshold = EDGE_THRESHOLD,
}: UseResizeOptions) {
  const [isResizing, setIsResizing] = useState(false)
  const [cursor, setCursor] = useState<string | null>(null)
  const edgeRef = useRef<Edge | null>(null)
  const stateRef = useRef<{
    edge: Edge
    startX: number
    startY: number
    width: number
    height: number
    posX: number
    posY: number
  } | null>(null)

  useEffect(() => {
    if (!isResizing) return

    const activeCursor = cursor
    if (activeCursor) document.body.style.cursor = activeCursor

    const handlePointerMove = (event: PointerEvent) => {
      const s = stateRef.current
      if (!s) return

      const dx = event.clientX - s.startX
      const dy = event.clientY - s.startY

      let w = s.width
      let h = s.height
      let x = s.posX
      let y = s.posY

      if (s.edge.right) w = s.width + dx
      else if (s.edge.left) { w = s.width - dx; x = s.posX + dx }

      if (s.edge.bottom) h = s.height + dy
      else if (s.edge.top) { h = s.height - dy; y = s.posY + dy }

      // Clamp to min size, correcting position for left/top edges
      if (w < minSize.width) { if (s.edge.left) x -= minSize.width - w; w = minSize.width }
      if (h < minSize.height) { if (s.edge.top) y -= minSize.height - h; h = minSize.height }

      // Clamp position to viewport
      x = Math.max(x, EDGE_MARGIN)
      y = Math.max(y, EDGE_MARGIN)

      // Clamp size to remaining viewport space
      w = Math.min(w, Math.max(window.innerWidth - x - EDGE_MARGIN, minSize.width))
      h = Math.min(h, Math.max(window.innerHeight - y - EDGE_MARGIN, minSize.height))

      onSizeChange?.({ width: w, height: h })
      if (s.edge.left || s.edge.top) onPositionChange?.({ x, y })
    }

    const handlePointerUp = () => {
      stateRef.current = null
      document.body.style.cursor = ''
      setIsResizing(false)
      setCursor(null)
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    return () => {
      document.body.style.cursor = ''
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
    }
  }, [isResizing, cursor, onSizeChange, onPositionChange, minSize])

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!enabled || isResizing) return
      const el = elementRef.current
      if (!el) return
      const edge = detectEdge(el, event.clientX, event.clientY, edgeThreshold)
      edgeRef.current = edge
      setCursor(edge ? cursorForEdge(edge) : null)
    },
    [enabled, isResizing, elementRef, edgeThreshold]
  )

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!enabled || !edgeRef.current) return
      event.preventDefault()
      event.stopPropagation()
      const edge = edgeRef.current
      stateRef.current = {
        edge,
        startX: event.clientX,
        startY: event.clientY,
        width: size.width,
        height: size.height,
        posX: position.x,
        posY: position.y,
      }
      setCursor(cursorForEdge(edge))
      setIsResizing(true)
    },
    [enabled, size.width, size.height, position.x, position.y]
  )

  const handlePointerLeave = useCallback(() => {
    if (!isResizing) {
      edgeRef.current = null
      setCursor(null)
    }
  }, [isResizing])

  return {
    isResizing,
    resizeCursor: cursor,
    resizeHandlers: {
      onPointerMove: handlePointerMove,
      onPointerDown: handlePointerDown,
      onPointerLeave: handlePointerLeave,
    },
  }
}
